import {
  Badge,
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Icon,
  Input,
  Text,
} from "@chakra-ui/react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FaRegComment } from "react-icons/fa";
import { MinimumRequirementResponseType, type MinimumRequirementDto } from "../../../autogen/bff-api";
import { SelectableCard } from "../../input/SelectableCard";
import { TipTap } from "../../input/TipTap/TipTap";
import { MINIMUM_REQUIREMENT_RESPONSE_TYPE_OPTIONS } from "../../../pages/sourcing-events/buyer/wizard/utils/publicProcurementOptions";
import { FormSectionBand } from "./FormSectionBand";

// t('Title') t('Minimum requirement title') t('Title is required') t('Description') t('optional')
// t('Response type') t('Type is required') t('Options')
// Response-type keys are resolved via variables, so pin them for i18next-parser:
// t('Yes / No') t('A simple yes or no answer.') t('Yes / No + comment')
// t('Yes or no, with room for an explanatory comment.') t('Respondent gets a comment field')
// t('Yes or no, with the document that proves it.') t('Respondent must attach a document on Yes')

export interface MinimumRequirementFormValues {
  responseType: MinimumRequirementResponseType;
  title: string;
  description: string;
}

interface Props {
  /** Lets a button outside the form submit it, so the same fields work in a drawer, a modal and a tab. */
  formId: string;
  defaultValues?: MinimumRequirementFormValues;
  onSubmit: (values: Omit<MinimumRequirementDto, "id">) => Promise<void> | void;
  /**
   * FOA § 23-7 only bites where negotiation is legal, so the marker is offered for the negotiating
   * procedures and stays out of the way everywhere else.
   */
  /** Rendered inside the form, under the fields — where the "save to the library" choice belongs. */
  children?: React.ReactNode;
}

const emptyDefaults: MinimumRequirementFormValues = {
  responseType: MinimumRequirementResponseType.YesNo,
  title: "",
  description: "",
};

/**
 * The one definition of what a minimum requirement asks for. Shared by the wizard's edit drawer, the
 * requirement library's modal and the "write a new one" tab — the fields drifted between copies before.
 */
export const MinimumRequirementFields = ({
  formId,
  defaultValues,
  onSubmit,
  children,
}: Props) => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<MinimumRequirementFormValues>({ defaultValues: defaultValues ?? emptyDefaults });

  const submitHandler = handleSubmit(async (values) => {
    await onSubmit({
      title: values.title,
      description: values.description,
      responseType: values.responseType,
    });
  });

  return (
    <form id={formId} data-testid="minimum-requirement-form" onSubmit={submitHandler}>
      <FormControl mb="6" isRequired isInvalid={errors.title !== undefined}>
        <FormLabel>{t("Title")}</FormLabel>
        <Input
          autoFocus
          autoComplete="off"
          data-1p-ignore
          placeholder={t("Minimum requirement title") ?? ""}
          {...register("title", { required: t("Title is required") ?? "" })}
        />
        <FormErrorMessage>{errors.title?.message}</FormErrorMessage>
      </FormControl>

      <FormControl mb="6">
        <FormLabel>
          {t("Description")}{" "}
          <Text as="span" color="smMuted" fontWeight="normal">
            {t("optional")}
          </Text>
        </FormLabel>
        <Controller
          name="description"
          control={control}
          render={({ field: { value, onChange } }) => (
            <TipTap
              content={value}
              onUpdate={onChange}
              editable={true}
              toolbar={{
                underline: false,
                headings: false,
                colors: false,
                link: false,
                table: false,
                image: false,
                template: false,
              }}
              debounceWait={0}
            />
          )}
        />
      </FormControl>

      <FormControl mb="6" isRequired isInvalid={errors.responseType !== undefined}>
        <FormLabel>{t("Response type")}</FormLabel>
        {/* Stacked, not side by side: the form also lives in a 448px panel, where two cards side by
            side leave the labels one character wide. */}
        <Controller
          name="responseType"
          control={control}
          rules={{ required: t("Type is required") ?? "" }}
          render={({ field: { value, onChange } }) => (
            <Flex direction="column" gap="3">
              {MINIMUM_REQUIREMENT_RESPONSE_TYPE_OPTIONS.map((option) => {
                const selected = value === option.value;
                return (
                  <SelectableCard
                    key={option.value}
                    isSelected={selected}
                    type="button"
                    w="full"
                    data-testid={`response-type-${option.value}`}
                    onClick={() => onChange(option.value)}
                  >
                    <Flex align="center" gap="3">
                      <Flex
                        align="center"
                        justify="center"
                        flexShrink={0}
                        boxSize="5"
                        borderRadius="full"
                        borderWidth="2px"
                        borderColor={selected ? "smPrimary" : "smBorder"}
                      >
                        {selected && <Box boxSize="2.5" borderRadius="full" bg="smPrimary" />}
                      </Flex>
                      <Box flex="1" minW="0" textAlign="left">
                        <Text fontWeight="bold">{t(option.labelKey)}</Text>
                        <Text fontSize="sm" color="smMuted">
                          {t(option.descriptionKey)}
                        </Text>
                      </Box>
                      {option.hintKey && (
                        <Badge
                          display="flex"
                          alignItems="center"
                          gap="1.5"
                          bg="smPrimarySofter"
                          color="smPrimary"
                          px="2.5"
                          py="1"
                          borderRadius="md"
                          fontWeight="medium"
                          fontSize="xs"
                          textTransform="none"
                        >
                          <Icon as={FaRegComment} />
                          {t(option.hintKey)}
                        </Badge>
                      )}
                    </Flex>
                  </SelectableCard>
                );
              })}
            </Flex>
          )}
        />
        <FormErrorMessage>{errors.responseType?.message}</FormErrorMessage>
      </FormControl>


      {/* The band only earns its place when there is a choice under it — the edit drawer passes none. */}
      {children && (
        <>
          <FormSectionBand>{t("Options")}</FormSectionBand>
          {children}
        </>
      )}
    </form>
  );
};
